/**
 * Segment of message parts to render as a single unit.
 *
 * - `passthrough`: a non-collapsible part (text, reasoning, non-collapsible
 *   tool, etc.) rendered via `MessagePrimitive.PartByIndex` with the same
 *   component overrides the current thread uses — byte-identical to today.
 * - `collapsible-run`: one or more consecutive collapsible tool parts rendered
 *   together inside a `CollapsibleToolRun`.
 */
export type PartSegment = { kind: 'passthrough'; index: number } | { kind: 'collapsible-run'; indices: number[] };

/**
 * Walk parts left-to-right and group consecutive collapsible parts into runs;
 * every other part becomes its own `passthrough` segment.
 *
 * Pure reducer extracted from `useGroupedParts` so it can be unit-tested
 * without a React/assistant-ui test harness — the hook wrapper provides the
 * closure that decides collapsibility for the current message context.
 */
export function groupParts<PartType>(
  parts: readonly PartType[],
  isCollapsible: (part: PartType) => boolean,
): PartSegment[] {
  return parts.reduce<PartSegment[]>((segments, part, index) => {
    if (!isCollapsible(part)) {
      segments.push({ kind: 'passthrough', index });
      return segments;
    }
    const last = segments.at(-1);
    if (last?.kind === 'collapsible-run') {
      last.indices.push(index);
    } else {
      segments.push({ kind: 'collapsible-run', indices: [index] });
    }
    return segments;
  }, []);
}
